import React from "react";
import { useIntl } from "react-intl";
import { AxiosError } from "axios";

import { type CartonPropertyGroupConfiguration } from "@/schemas/carton-property-group";
import { useProductionGroups } from "@/hooks/production-groups";

import { CartonPropertyGroupsConfigurationModalTemplate } from "./carton-property-groups-configuration-modal.template";

export interface CartonPropertyGroupsConfigurationModalProps {
	readonly open: boolean;
	readonly defaultValues?: CartonPropertyGroupConfiguration;
	readonly onSubmit: (data: CartonPropertyGroupConfiguration) => Promise<void>;
	readonly onClose: () => void;
	readonly error: Error | null;
}

export const CartonPropertyGroupsConfigurationModal: React.FC<CartonPropertyGroupsConfigurationModalProps> = ({
	open,
	onClose,
	onSubmit,
	defaultValues,
	error,
}) => {
	const intl = useIntl();
	const { productionGroups } = useProductionGroups();

	const getErrorMessage = (error: Error | null): string | undefined => {
		if (error === null) {
			return undefined;
		}

		if (error instanceof AxiosError && error.status === 409) {
			return intl.formatMessage({
				id: "Failed to save Carton Property Group. A Carton Property Group already exists with this name",
				defaultMessage: "Failed to save Carton Property Group. A Carton Property Group already exists with this name",
			});
		}

		if (error instanceof AxiosError && error.status === 404) {
			return intl.formatMessage({
				id: "Failed to save Carton Property Group. Carton Property Group not found",
				defaultMessage: "Failed to save Carton Property Group. Carton Property Group not found",
			});
		}

		if (error instanceof AxiosError && error.status === 400) {
			return intl.formatMessage({
				id: "Failed to save Carton Property Group. Bad request",
				defaultMessage: "Failed to save Carton Property Group. Bad request",
			});
		}

		return intl.formatMessage({
			id: "Failed to save Carton Property Group, please try again",
			defaultMessage: "Failed to save Carton Property Group, please try again",
		});
	};

	return (
		<CartonPropertyGroupsConfigurationModalTemplate
			open={open}
			defaultValues={defaultValues}
			productionGroups={productionGroups}
			onClose={onClose}
			onSubmit={onSubmit}
			errorMessage={getErrorMessage(error)}
		/>
	);
};
